import React, { useState } from 'react';
import { CSSTransition } from 'react-transition-group';

import { Author } from 'js/search/react/canonical_work_results';
import AuthorPopover from './popovers/author_popover';

type PopoverProps = {
  enablePopover: true;
  fetchActorData: (author: Author) => void;
  followUser: (id: number | string, callback: () => void) => void;
  unfollowUser: (id: number | string, callback: () => void) => void;
};

type BaseAuthorNameProps = {
  author: Author;
  addClickTracking: (url: string) => string;
  loggedIn?: boolean;
  isOffsiteAuthor?: boolean;
};

type AuthorNameProps =
  | (BaseAuthorNameProps & PopoverProps)
  | (BaseAuthorNameProps & { enablePopover: false });

const AuthorName: React.FC<AuthorNameProps> = (props) => {
  const {
    author,
    addClickTracking,
    loggedIn = true,
    isOffsiteAuthor = false,
  } = props;

  const [showPopover, setShowPopover] = useState(false);

  const handleMouseEnter = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowPopover(true);
  };

  const handleMouseLeave = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowPopover(false);
  };

  // For ownerless works, external authors don't have URLs
  if (!author.url) {
    return (
      <div className="work-card--author-outer-wrapper">
        <div className="work-card--author-inner-wrapper">
          {author.photo && (
            <img
              className="work-card--profile-image-sm"
              src={author.photo}
              alt={author.displayName}
            />
          )}
          <div className="work-card--author-name">{author.displayName}</div>
        </div>
      </div>
    );
  }

  if (props.enablePopover) {
    return (
      <div
        className="work-card--author-outer-wrapper"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <a href={addClickTracking(author.url)} className="work-card--link">
          <div className="work-card--author-inner-wrapper">
            <img
              className="work-card--profile-image-sm"
              src={author.photo}
              alt={author.displayName}
            />
            <div className="work-card--author-name">{author.displayName}</div>
          </div>
        </a>
        <CSSTransition
          classNames="work-card--author-popover-outer-wrapper"
          in={showPopover}
          timeout={{ enter: 200, exit: 200 }}
          unmountOnExit
        >
          <AuthorPopover
            author={author}
            fetchActorData={props.fetchActorData}
            followUser={props.followUser}
            unfollowUser={props.unfollowUser}
            addClickTracking={addClickTracking}
            loggedIn={loggedIn}
            isOffsiteAuthor={isOffsiteAuthor}
          />
        </CSSTransition>
      </div>
    );
  } else {
    return (
      <div className="work-card--author-outer-wrapper">
        <a href={addClickTracking(author.url)} className="work-card--link">
          <div className="work-card--author-inner-wrapper">
            <img
              className="work-card--profile-image-sm"
              src={author.photo}
              alt={author.displayName}
            />
            <div className="work-card--author-name">{author.displayName}</div>
          </div>
        </a>
      </div>
    );
  }
};

type BaseAuthorListProps = {
  authors: Author[];
  addClickTracking: (url: string) => string;
  loggedIn?: boolean;
  isOffsiteAuthor?: boolean;
};

type AuthorListProps =
  | (BaseAuthorListProps & PopoverProps)
  | (BaseAuthorListProps & { enablePopover: false });

const AuthorList: React.FC<AuthorListProps> = (props) => {
  const {
    authors,
    addClickTracking,
    loggedIn = true,
    isOffsiteAuthor = false,
  } = props;

  if (props.enablePopover) {
    return (
      <div className="work-card--authors-list">
        {authors.map((author) => (
          <AuthorName
            key={author.id}
            author={author}
            fetchActorData={props.fetchActorData}
            followUser={props.followUser}
            unfollowUser={props.unfollowUser}
            addClickTracking={addClickTracking}
            enablePopover={true}
            loggedIn={loggedIn}
            isOffsiteAuthor={isOffsiteAuthor}
          />
        ))}
      </div>
    );
  } else {
    return (
      <div className="work-card--authors-list">
        {authors.map((author) => (
          <AuthorName
            key={author.id}
            author={author}
            addClickTracking={addClickTracking}
            enablePopover={false}
            loggedIn={loggedIn}
            isOffsiteAuthor={isOffsiteAuthor}
          />
        ))}
      </div>
    );
  }
};

export default AuthorList;
